import { ChangeEvent, forwardRef } from "react";
import { useFormContext } from "react-hook-form";
import { getRoleType } from "services/TenantSubmitHandler";
import { useAppSelector } from "store/configureStore";
import { FormFieldValidation } from "store/formTemplate/formTemplateTypes";
import { sanitizeForm } from "utils/htmlSanitizer";
import { getDefaultValue } from "utils/renderTemplates";
import { getNestedError } from "utils/splitDot";
import { mapValidations } from "utils/validationMap";

type FieldVariant = "editable" | "readonly" | "disabled";
type FieldAs = "input" | "textarea";

/**
 * Props for the InputField component.
 *
 * @property id - Unique identifier for the field, used for accessibility.
 * @property name - Optional name attribute for the field.
 * @property label - Optional label text to display for the field.
 * @property type - Input type (e.g., "text", "password"). Applies only if `as="input"`.
 * @property value - Current value of the field.
 * @property placeholder - Placeholder text to display when the field is empty.
 * @property variant - Visual variant of the field (custom type `FieldVariant`).
 * @property rows - Number of rows for a textarea. Applies only if `as="textarea"`.
 * @property as - Determines the element type: "input" or "textarea" (custom type `FieldAs`).
 * @property onChange - Callback fired when the field value changes. Receives the new value as a string.
 * @property disabled - If true, disables the field.
 * @property error - Error message to display below the field.
 * @property className - Optional CSS class name(s) for custom styling.
 * @property animalIndex - Index for handling multiple animal profiles (used in forms with multiple animals).
 */
interface FieldProps {
  id: string; // id for accessibility
  name?: string;
  label?: string;
  type?: string; // only applies if as="input"
  value?: string;
  placeholder?: string;
  variant?: FieldVariant;
  rows?: number; // only applies if as="textarea"
  as?: FieldAs; // determines input or textarea
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  error?: string;
  className?: string;
  animalIndex?: number; // for handling multiple animal profiles
  validations?: FormFieldValidation; // validation rules for react-hook-form
  animalType?: string; // for handling multiple animal profiles
  defaultValue?: string;
  clearOnFill?: string[]; // fields to clear when this field is filled
  disableForRole?: string[]; // roles for which the field should be disabled
  atLeastOneOf?: string; // field name that has atLeastOneOf relationship with this field
}

/**
 * InputField is a reusable form component that renders either an input or textarea field with customizable props.
 * It supports editable, readonly, and disabled variants, displays labels and error messages, and forwards refs.
 *
 * @param id - The unique identifier for the input or textarea element.
 * @param name - The name attribute for the input or textarea element.
 * @param label - Optional label text displayed above the field.
 * @param type - The type of input (e.g., 'text', 'number', etc.). Defaults to 'text'.
 * @param value - The value of the input or textarea field.
 * @param placeholder - Placeholder text for the field.
 * @param variant - Field variant: 'editable', 'readonly', or 'disabled'. Defaults to 'editable'.
 * @param rows - Number of rows for textarea. Defaults to 3.
 * @param as - Determines whether to render an 'input' or 'textarea'. Defaults to 'input'.
 * @param onChange - Callback fired when the field value changes.
 * @param disabled - Whether the field is disabled. Defaults to false.
 * @param error - Error message to display below the field.
 * @param className - Additional CSS classes for custom styling.
 * @param animalIndex - Optional index for animal-related forms.
 * @param rest - Additional props spread onto the input or textarea element.
 * @param ref - Ref forwarded to the input or textarea element.
 *
 * @returns A JSX element containing a styled input or textarea field with label and error support.
 */
const InputField = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  FieldProps
>(
  (
    {
      id,
      name,
      label,
      type = "text",
      value = "",
      placeholder = "",
      variant = "editable",
      rows = 3,
      as = "input",
      onChange,
      disabled = false,
      error,
      className = "",
      animalIndex,
      animalType,
      validations,
      defaultValue,
      clearOnFill,
      disableForRole,
      atLeastOneOf,
      ...rest
    },
    ref
  ) => {

    const stageOnePetsData = useAppSelector((state) => state.Tenant.tenantPets);

    const baseClass = as === "textarea" ? "input-field min-h-75px" : "input-field";
    const fieldClass = [
      baseClass,
      `${baseClass}-${variant}`,
      disabled ? `${baseClass}-disabled min-h-75px` : "",
      error ? `${baseClass}-error min-h-75px` : "",
      className,
    ]
      .filter(Boolean)
      .join(" ");

    // Access form context for validation and error handling
    const {
      register,
      formState: { errors },
      setValue,
      trigger
    } = useFormContext();
    const errorMessage =
      name && getNestedError(errors, name)?.message
        ? getNestedError(errors, name).message
        : undefined;

    

    const handleDynamicClear = (e: React.ChangeEvent<HTMLInputElement>, value: string) => {
      const choiseFieldName = e.target.name;
      const [petType, , indexStr] = choiseFieldName.split(".");
      const index = Number(indexStr);
      
      if (value !== "" && clearOnFill && clearOnFill.length > 0) {
        clearOnFill.forEach((fieldName: string) => {
          const fieldNameEl = petType + "." + fieldName + "." + index;
          setValue(fieldNameEl, "");
          const inputEl = document.querySelector(`input[name="${fieldNameEl}"]`);
          if (inputEl && inputEl instanceof HTMLInputElement) {
            inputEl.value = "";
          }
        });
      }
    };
 
    const computedDefaultValue = defaultValue === "" ? getDefaultValue(name ?? "", stageOnePetsData ?? []) : defaultValue;
 
       
    return (
      <div className="d-flex flex-column gap-10px">
        {label && (
          <label className="d-block fs-16px fw-normal text-black" htmlFor={id} dangerouslySetInnerHTML={{ __html: sanitizeForm(label) }} />
        )}
        <div className="d-flex flex-column">
          {as === "textarea" ? (
            (() => {
              const registered = name ? register(name, mapValidations(validations ? [validations] : [])) : null;
              return (
              <textarea
              id={id}
              className={fieldClass}
              placeholder={placeholder}
              rows={rows}
              readOnly={variant === "readonly"}
              disabled={disabled || variant === "disabled"}
              {...(registered ? { ...registered, defaultValue: computedDefaultValue, onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => {
                registered.onChange(e);
                onChange(e as any);
                handleDynamicClear(e as any, e.target.value);

                // If this field has atLeastOneOf relationship, trigger validation on the related field
                if (atLeastOneOf && name) {
                  const [petType, , indexStr] = name.split(".");
                  const index = Number(indexStr);
                  const relatedFieldName = petType + "." + atLeastOneOf + "." + index;
                  trigger(relatedFieldName);
                }
              } } : { value, onChange: onChange ? (e: React.ChangeEvent<HTMLTextAreaElement>) => onChange(e as any) : undefined })}
              ref={ref as React.Ref<HTMLTextAreaElement>}
            />
              );
            })()
          ) : (
            (() => {
              const registered = name ? register(name, mapValidations(validations ? [validations] : [])) : null;
              return (
            <input
              id={id}
              type={type}
              className={fieldClass}
              placeholder={placeholder}
              readOnly={variant === "readonly"}
              disabled={disabled || variant === "disabled" || disableForRole?.includes(getRoleType())}
              {...(registered ? { ...registered, defaultValue: computedDefaultValue, onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                registered.onChange(e);
                onChange(e);
                handleDynamicClear(e, e.target.value);

                // If this field has atLeastOneOf relationship, trigger validation on the related field
                if (atLeastOneOf && name) {
                  const [petType, , indexStr] = name.split(".");
                  const index = Number(indexStr);
                  const relatedFieldName = petType + "." + atLeastOneOf + "." + index;
                  trigger(relatedFieldName);
                }
              } } : { value, onChange })}
              ref={ref as React.Ref<HTMLInputElement>}
              autoComplete="off"
            />
              );
            })()
          )}
          {(errorMessage || error) && (
            <div className="text-red fs-14px">{errorMessage || error}</div>
          )}
        </div>
      </div>
    );
  }
);

export default InputField;
